import {
    FloatingCard,
    iconSizeSm,
    joinClassNames,
    renderDateTime,
    SearchableCardList,
    SelectionCard,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconBox } from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';
import {
    type ContainerStockLine,
    filterStockLines,
    lastStockMovement,
    plannedActionOf,
    sortStockLines,
} from '../../containerOrder';
import classes from '../ContainerSteps.module.css';
import { type ContainerStockView } from './StockStepBody';

interface StockLineListProps {
    stock: ContainerStockView;
    /** The line the order has picked, which stands out in the list. */
    selectedLineId: string | undefined;
    /** Whether the picked line is one the dispatcher has to look at. */
    selectedNeedsReview?: boolean;
    /** What follows the list, such as the way out for a container that is not recorded. */
    footer?: ReactNode;
    /** Sets the list apart while something else holds the customer's attention. */
    dimmed?: boolean;
    onSelect: (line: ContainerStockLine) => void;
}

/** The containers recorded and planned at the site, searchable, one card per container or pool. */
// @ai-generated 2026-10-08T22:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function StockLineList({
    stock,
    selectedLineId,
    selectedNeedsReview = false,
    footer,
    dimmed = false,
    onSelect,
}: StockLineListProps) {
    const i18n = useTranslate('');
    const [search, setSearch] = useState('');

    const sortedLines = useMemo(() => sortStockLines(stock.lines), [stock.lines]);
    const visibleLines = useMemo(() => filterStockLines(sortedLines, search), [sortedLines, search]);
    const lastMovement = useMemo(() => lastStockMovement(stock.lines), [stock.lines]);

    const stamp = lastMovement
        ? `${i18n('Stand laut unseren Aufzeichnungen · letzte Bewegung')} ${renderDateTime(lastMovement, 'date')}`
        : i18n('Stand laut unseren Aufzeichnungen');

    return (
        <FloatingCard
            title={i18n('Deine Container vor Ort')}
            subtitle={stamp}
            separateHeader={false}
            collapsible={false}
            className={joinClassNames(classes.cardHeaderStretch, dimmed ? classes.stockCardDimmed : '')}
        >
            <SearchableCardList
                name="container_stock"
                label={i18n('Deine Container vor Ort')}
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder={i18n('Behälter suchen …')}
                listLabel={`${visibleLines.length} ${i18n('Behälter')}`}
                isEmpty={!stock.isLoading && visibleLines.length === 0}
                emptyText={i18n('Kein passender Container gefunden')}
                isLoadingMore={stock.isLoading}
                footer={footer}
            >
                {visibleLines.map((line) => (
                    <SelectionCard
                        key={line.id}
                        icon={<IconBox size={iconSizeSm} />}
                        title={line.containerTypeLabel}
                        subtitle={stockLineDetail(line, i18n)}
                        selected={selectedLineId === line.id}
                        badge={badgeOf(line, selectedLineId === line.id && selectedNeedsReview, i18n)}
                        onClick={() => onSelect(line)}
                    />
                ))}
            </SearchableCardList>
        </FloatingCard>
    );
}

const badgeOf = (line: ContainerStockLine, needsReview: boolean, i18n: (key: string) => string) => {
    if (needsReview) {
        return i18n('Wird geprüft');
    }

    return plannedActionOf(line) ? i18n('Geplant') : undefined;
};

/**
 * What a line says about itself under its type: the number of the one container it stands for, how
 * many of a counted pool are on site, or with which requisition a planned one comes.
 */
// @ai-generated 2026-10-21T11:23:08+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
function stockLineDetail(line: ContainerStockLine, i18n: (key: string) => string): string {
    const planned = plannedActionOf(line);
    if (planned) {
        const count = line.stockCount > 1 ? `${line.stockCount} ${i18n('Container')} · ` : '';
        return planned.requisition_number
            ? `${count}${i18n('kommt mit Bestellung')} ${planned.requisition_number}`
            : `${count}${i18n('geplant')}`;
    }

    return line.usesIndividualManagement
        ? `${i18n('Nr.')} ${line.containerNumber}`
        : `${line.stockCount} ${i18n('vor Ort')} · ${i18n('ohne Nummer geführt')}`;
}
